import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button, Field, Input,
  MessageBar, MessageBarBody, Select, Spinner, Text, makeStyles, tokens, useId,
} from '@fluentui/react-components';
import { ArrowClockwise20Regular } from '@fluentui/react-icons';
import { useAuthArgs } from '../../hooks/useAuthArgs';
import {
  getRoutingDecisions, normalizeRoutingTraceId,
  type RoutingDecisionsResponse,
} from '../../api/routingDecisionsClient';
import { outcomeExplanation } from './routingDecisionPresentation';
import { RecordedTime, RoutingDecisionDetails } from './RoutingDecisionDetails';

const useStyles = makeStyles({
  page: { minWidth: 0, display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL },
  heading: { margin: 0 },
  toolbar: {
    display: 'flex', alignItems: 'end', flexWrap: 'wrap', gap: tokens.spacingHorizontalM,
  },
  traceField: { flexGrow: 1, maxWidth: '460px', minWidth: 0 },
  summary: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: tokens.spacingHorizontalM, margin: 0,
    padding: tokens.spacingHorizontalM, borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  metric: { minWidth: 0, overflowWrap: 'anywhere', '& dt': { color: tokens.colorNeutralForeground2 }, '& dd': { margin: 0 } },
  layout: {
    display: 'grid', gridTemplateColumns: 'minmax(260px, 1fr) minmax(0, 2fr)',
    alignItems: 'start', gap: tokens.spacingHorizontalL,
    '@media (max-width: 1100px)': { gridTemplateColumns: 'minmax(0, 1fr)' },
  },
  list: { listStyleType: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  row: { width: '100%', justifyContent: 'start', textAlign: 'left', whiteSpace: 'normal', minWidth: 0 },
  rowContent: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS, minWidth: 0, overflowWrap: 'anywhere' },
  detail: {
    minWidth: 0, padding: tokens.spacingHorizontalM, border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium, overflowWrap: 'anywhere',
    display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM,
  },
  helper: { color: tokens.colorNeutralForeground2 },
});

export function RoutingDecisionsPage() {
  const styles = useStyles();
  const headingId = useId('routing-decisions');
  const detailId = useId('routing-decision-detail');
  const getAuthArgs = useAuthArgs();
  const [traceId, setTraceId] = useState('');
  const [limit, setLimit] = useState(50);
  const [appliedTrace, setAppliedTrace] = useState('');
  const [data, setData] = useState<RoutingDecisionsResponse | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validation, setValidation] = useState<string | null>(null);
  const [loadedAt, setLoadedAt] = useState<string | null>(null);
  const request = useRef<AbortController | null>(null);
  const selected = data?.records.find((record) => record.id === selectedId);

  const refresh = useCallback(async (trace: string, recordLimit: number) => {
    let normalized: string;
    try {
      normalized = normalizeRoutingTraceId(trace);
    } catch (err) {
      setValidation(err instanceof Error ? err.message : 'Invalid trace ID.');
      return;
    }
    setValidation(null);
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    const current = () => !controller.signal.aborted && request.current === controller;
    setLoading(true);
    setError(null);
    setData(null);
    setSelectedId(null);
    setLoadedAt(null);
    setAppliedTrace(normalized);
    try {
      const auth = await getAuthArgs();
      if (!current()) return;
      if (!auth) throw new Error('Sign in again to view routing decisions.');
      const next = await getRoutingDecisions(auth, { traceId: normalized, limit: recordLimit, signal: controller.signal });
      if (!current()) return;
      setData(next);
      setLoadedAt(new Date().toISOString());
    } catch (err) {
      if (current()) setError(err instanceof Error ? err.message : 'Could not load routing decisions. Try refreshing.');
    } finally {
      if (current()) setLoading(false);
    }
  }, [getAuthArgs]);

  useEffect(() => {
    void refresh('', 50);
    return () => request.current?.abort();
  }, [refresh]);

  return (
    <section className={styles.page} aria-labelledby={headingId}>
      <div>
        <Text as="h1" id={headingId} size={600} weight="semibold" className={styles.heading}>Why this route</Text>
        <Text className={styles.helper}>Read-only routing diagnostics. Opening or refreshing this page does not run inference or change routing.</Text>
      </div>
      <MessageBar intent="info">
        <MessageBarBody>
          Sampled, incomplete data from one replica, not a fleet-wide audit. Records expire and may be dropped or truncated.
          Unvisited and ineligible endpoints are omitted; only recorded evaluations are shown.
          First-token acceptance does not mean the stream completed. No customer prompts or message details are included.
        </MessageBarBody>
      </MessageBar>
      <form className={styles.toolbar} onSubmit={(event) => { event.preventDefault(); void refresh(traceId, limit); }}>
        <Field label="Trace ID (optional)" className={styles.traceField}
          validationState={validation ? 'error' : 'none'}
          validationMessage={validation ?? '32 hexadecimal characters; leave empty for recent decisions.'}>
          <Input value={traceId} onChange={(_, value) => { setTraceId(value.value); setValidation(null); }}
            autoComplete="off" spellCheck={false} />
        </Field>
        <Field label="Maximum records">
          <Select value={String(limit)} onChange={(_, value) => setLimit(Number(value.value))}>
            <option value="25">25</option><option value="50">50</option><option value="100">100</option>
          </Select>
        </Field>
        <Button type="submit" appearance="primary" icon={<ArrowClockwise20Regular />} disabled={loading}>
          {loading ? 'Refreshing…' : 'Refresh'}
        </Button>
      </form>
      <Text size={200} className={styles.helper}>Manual refresh only — no background polling. Results describe the serving replica, not a selected configuration layer.</Text>
      {loading && <Spinner label="Loading routing decisions…" />}
      {error && <MessageBar intent="error"><MessageBarBody>{error}</MessageBarBody></MessageBar>}
      {data && (
        <>
          <dl className={styles.summary} aria-label="Diagnostics status">
            <div className={styles.metric}><dt>Serving replica</dt><dd>{data.replica}</dd></div>
            <div className={styles.metric}><dt>Capture</dt><dd>{data.enabled ? 'Enabled' : 'Disabled'}</dd></div>
            <div className={styles.metric}><dt>Sample rate</dt><dd>{(data.sampleRate * 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}%</dd></div>
            <div className={styles.metric}><dt>Retention</dt><dd>{data.retentionSeconds.toLocaleString()} seconds</dd></div>
            <div className={styles.metric}><dt>Record capacity</dt><dd>{data.maxRecords.toLocaleString()}</dd></div>
            <div className={styles.metric}><dt>Dropped records / updates</dt><dd>{data.droppedRecords.toLocaleString()}</dd></div>
            {data.truncatedRecords !== undefined && <div className={styles.metric}><dt>Truncated records</dt><dd>{data.truncatedRecords.toLocaleString()}</dd></div>}
            {data.queueCapacity !== undefined && <div className={styles.metric}><dt>Queue capacity</dt><dd>{data.queueCapacity.toLocaleString()}</dd></div>}
            {data.maxCandidates !== undefined && <div className={styles.metric}><dt>Candidate cap per record</dt><dd>{data.maxCandidates.toLocaleString()}</dd></div>}
          </dl>
          {!data.enabled && <MessageBar intent="warning"><MessageBarBody>Diagnostic capture is disabled and no records are retained. Viewing this page does not enable collection.</MessageBarBody></MessageBar>}
          <Text role="status" size={200}>
            {data.records.length} recorded decisions{appliedTrace ? ` for trace ${appliedTrace}` : ''}.
            {loadedAt && <> Refreshed <RecordedTime value={loadedAt} />.</>}
          </Text>
          {data.records.length === 0 ? (
            <MessageBar intent="info"><MessageBarBody>
              No recorded decisions match this query on this replica. Sampling, retention, disabled capture, or another
              replica may explain missing records. No record does not mean no routing occurred.
            </MessageBarBody></MessageBar>
          ) : (
            <div className={styles.layout}>
              <div>
                <Text as="h2" size={400} weight="semibold">Recorded decisions</Text>
                <ul className={styles.list} aria-label="Recorded routing decisions">
                  {data.records.map((record) => {
                    const outcome = outcomeExplanation(record.outcome);
                    return (
                      <li key={record.id}>
                        <Button className={styles.row} appearance={selectedId === record.id ? 'primary' : 'secondary'}
                          aria-pressed={selectedId === record.id} aria-controls={detailId}
                          onClick={() => setSelectedId(record.id)}>
                          <span className={styles.rowContent}>
                            <strong>{record.requestedModel || 'Unspecified model'}</strong>
                            <span>{record.agentRef || 'Unspecified agent'} · <RecordedTime value={record.startedAtUtc} /></span>
                            <span>{outcome.label} · Selection {record.elapsedMilliseconds.toLocaleString()} ms{record.truncated ? ' · Truncated' : ''}</span>
                            {record.attempt && <span>{outcomeExplanation(record.attempt.outcome).label}</span>}
                            <span>Trace: {record.traceId ?? 'Not recorded'}</span>
                          </span>
                        </Button>
                      </li>
                    );
                  })}
                </ul>
              </div>
              <div id={detailId} className={styles.detail}>
                {selected
                  ? <RoutingDecisionDetails key={selected.id} record={selected} />
                  : <Text>Select a recorded decision to inspect its evaluated candidates and attempt timeline.</Text>}
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}
